/**
 * Claims Structured Emission (pack code CSE, register row 0.99237) —
 * post-narrative-gen extractor that converts the rendered narrative
 * into `report.claims[]` with structured `{field, value, confidence,
 * sources[]}` shape.
 *
 * Why
 * ----
 * The `ReportClaim` type carries optional structured fields (field /
 * value / confidence / reportSectionId / id / reportId / emittedAt)
 * but no writer populated them — three downstream packs silently
 * degraded:
 *   1. RDX-1 `extractReportClaimsForRequirement` walks `report.claims[]`
 *      to inherit a prior report's field assertions into the next
 *      report's SED slots. With no writer, every typed report's
 *      cross-type inheritance lagged one cycle behind body-text
 *      re-parsing.
 *   2. RCO Path B (`computeBodyCitedDocIds`) scans `claim.sources[]`
 *      for `doc:<id>` prefixes — fed the empty set.
 *   3. EVL-1 Tier-1 lineage (`priorReports` row in the lineage chip
 *      strip) counts the same `doc:<id>` / `report:<id>` evidence
 *      across prior reports — fed the empty set.
 *
 * What
 * ----
 * One Gemini structured-output call per report at gen-time, AFTER
 * references are finalised and AFTER SNT's instruction-shape guard
 * stamps `sectionGuardStatus`. The model gets the rendered narrative,
 * the report-type's canonical SED field list (from
 * REPORT_TO_SED_MAPPING), and a `[Ref N] → source-prefix` map so it
 * can resolve in-text citations to lineage-target refs.
 *
 * Invariants
 * ----------
 *  - Every emitted claim carries ≥1 source. Zero-source claims are
 *    dropped.
 *  - Reproduce values verbatim (prompt constraint). The merge resolver
 *    (RDX-4) caps report-derived confidence at 0.7 so authored / doc
 *    evidence always wins on disagreement.
 *  - Quarantined sections (SNT-1 `sectionGuardStatus ===
 *    'degraded-instruction-leak'`) MUST be filtered out by the caller
 *    BEFORE invoking the extractor.
 *  - When the report type has no REPORT_TO_SED_MAPPING entries, the
 *    extractor returns `[]` without an API call. Same for empty
 *    references map.
 *
 * Fail-soft
 * ---------
 * The caller wraps `extractReportClaims` in try/catch and stamps
 * `report.claims = []` on any failure.
 *
 * CI-1 (CSE-FOLLOWUP-INCREMENTAL) — on rerun, sections of the
 * narrative whose body is byte-similar to the prior version reuse
 * prior claims; only changed sections drive a Gemini call. Identical
 * narrative → zero Gemini calls. Falls back to the full path on any
 * missing prior state. See `extractReportClaimsIncremental`.
 */
import { runGeminiJSON, Type, type Schema } from './geminiClient';
import type { SiteRagReport, DataSource } from '../types/siteRag';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import type { ReportClaim } from '../types/citation';
import type { ReportTypeId } from '../config/reportTypes';
import { REPORT_TO_SED_MAPPING } from '../lib/sed/reportToSedMapping';
import { resolveDocumentLineage } from '../lib/resolveDocumentLineage';

/* ── Constants ──────────────────────────────────────────────────────── */

const MAX_CLAIMS = 60;
const DEFAULT_MODEL = 'gemini-2.5-flash';

/* ── Schema ─────────────────────────────────────────────────────────── */

const CLAIM_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    claims: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          reportSectionId: { type: Type.STRING },
          field: { type: Type.STRING },
          value: { type: Type.STRING },
          confidence: { type: Type.NUMBER },
          sources: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
          },
        },
        required: ['reportSectionId', 'field', 'value', 'confidence', 'sources'],
      },
    },
  },
  required: ['claims'],
};

/* ── References map ─────────────────────────────────────────────────── */

export function buildReferencesMap(
  references: readonly DataSource[],
  sed: SiteEvidenceDocument | undefined,
): Map<number, string> {
  const out = new Map<number, string>();
  for (let i = 0; i < references.length; i += 1) {
    const ref = references[i]!;
    const refNum = i + 1;

    if (sed) {
      const lineage = resolveDocumentLineage(ref, sed);
      if (lineage?.docId) {
        out.set(refNum, `doc:${lineage.docId}`);
        continue;
      }
    }

    if (typeof ref.field === 'string' && /^doc[_:-]/.test(ref.field)) {
      const docId = ref.field.replace(/^doc[_:-]/, '');
      if (docId) {
        out.set(refNum, `doc:${docId}`);
        continue;
      }
    }

    const slug = slugProvider(ref.source ?? '');
    if (slug) {
      out.set(refNum, `provider:${slug}`);
    }
  }
  return out;
}

function slugProvider(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

/* ── Narrative blob ─────────────────────────────────────────────────── */

export function collectNarrativeSections(
  report: SiteRagReport,
): Array<{ sectionId: string; body: string }> {
  const out: Array<{ sectionId: string; body: string }> = [];
  const push = (sectionId: string, body: string): void => {
    const trimmed = body.trim();
    if (trimmed.length > 0) out.push({ sectionId, body: trimmed });
  };

  const r = report as unknown as Record<string, unknown>;
  if (typeof r['executiveSummary'] === 'string') push('executiveSummary', r['executiveSummary'] as string);
  if (typeof r['summary'] === 'string') push('summary', r['summary'] as string);
  if (typeof r['sitingNotes'] === 'string') push('sitingNotes', r['sitingNotes'] as string);
  if (typeof r['conclusion'] === 'string') push('conclusion', r['conclusion'] as string);

  const concObj = r['conclusion'];
  if (concObj && typeof concObj === 'object' && !Array.isArray(concObj)) {
    push('conclusion', objJoin(concObj as Record<string, unknown>));
  }

  for (const key of [
    'climateSevereWeather',
    'infrastructureInterconnection',
    'gridStudy',
    'transportStudy',
    'connectivityStudy',
    'nepaScreening',
    'ecologicalAssessment',
    'waterResources',
    'powerAnalysis',
    'waterAnalysis',
    'testFitAnalysis',
    'interconnectionAnalysis',
  ]) {
    const v = r[key];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      push(key, objJoin(v as Record<string, unknown>));
    }
  }

  return out;
}

function objJoin(o: Record<string, unknown>): string {
  return Object.values(o)
    .map(v => {
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : '')).join(' ');
      return '';
    })
    .join(' ');
}

/* ── Target field list ──────────────────────────────────────────────── */

export function getTargetFieldsForReportType(
  reportType: ReportTypeId | string | undefined,
): string[] {
  if (!reportType) return [];
  const mappings = (REPORT_TO_SED_MAPPING as Record<string, ReadonlyArray<{ sedFieldPath: string }>>)[
    reportType
  ];
  if (!Array.isArray(mappings)) return [];
  return Array.from(new Set(mappings.map(m => m.sedFieldPath)));
}

/* ── Prompt ─────────────────────────────────────────────────────────── */

export function buildExtractionPrompt(args: {
  sections: Array<{ sectionId: string; body: string }>;
  targetFields: string[];
  referencesMap: Map<number, string>;
}): string {
  const { sections, targetFields, referencesMap } = args;
  const sectionsBlock = sections.map(s => `## ${s.sectionId}\n${s.body}`).join('\n\n');
  const fieldsBlock = targetFields.map(f => `- ${f}`).join('\n');
  const refsBlock = [...referencesMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([n, src]) => `[${n}] -> ${src}`)
    .join('\n');

  return `You are extracting structured claims from a generated report.

REPORT SECTIONS (narrative):
${sectionsBlock}

TARGET FIELDS (extract values for these canonical SED paths only — skip a field if the narrative makes no concrete assertion about it):
${fieldsBlock}

REFERENCE MAP (the report uses [Ref N] superscripts; resolve each cited [Ref N] in the same sentence as the claim through this map):
${refsBlock}

For each TARGET FIELD that the narrative makes a concrete claim about, emit ONE record:
- reportSectionId: the section id from the headers above where the claim appears (e.g. "executiveSummary").
- field: the canonical SED path verbatim from the TARGET FIELDS list.
- value: the numeric value (as a string), verdict, or short string the narrative asserts. Reproduce values verbatim — do not infer values that aren't stated.
- grid.estimatedQueueTime is a site load-service assertion only: do not emit a context-only proximity heuristic or regional/generator queue benchmark as this field. Preserve those figures in their original narrative, with their source and scope.
- grid.estimatedCapacityMW is a site utility headroom assertion only: do not emit facility demand, IT load, transformer conversion references, standby/BTM generator nameplate, or multi-feeder aggregate envelope vs N-1 surviving-supply design figures as this field.
- confidence: 0..1. Higher when the narrative uses precise wording ("145 MW available", "verified by utility"); lower when hedged ("approximately", "around", "estimated").
- sources: array of 1+ resolved source prefixes. For every [Ref N] the narrative cites in the same sentence as the claim, look up N in the REFERENCE MAP and include the resolved 'doc:<id>'/'provider:<key>'/etc. string. Each entry MUST start with one of: doc:, provider:, sed:, sed-section:, kmz:, report:, dam:, baseline:, site-context:, ref:.

CRITICAL: drop any claim that would have zero sources. Do not fabricate sources. Do not include claims whose value is not stated in the narrative.`;
}

/** A proximity/regional duration is not a site load-service claim. Consult
 * only exact-value clauses in this same report section: another section or a
 * different quantity cannot suppress an actual utility assertion. */
export function isContextOnlyQueueClaim(
  field: string, value: unknown, sectionId: string,
  sections: ReadonlyArray<{ sectionId: string; body: string }>,
): boolean {
  if (field !== 'grid.estimatedQueueTime' || typeof value !== 'string') return false;
  const normalize = (text: string) => text.replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim().toLowerCase();
  const literal = normalize(value);
  if (!/^\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?\s+(?:months?|years?|yrs?)$/.test(literal)) return false;
  const escaped = literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const exactValue = new RegExp(`(?<![\\d.])${escaped}(?![a-z0-9])`, 'i');
  const body = sections.find(section => section.sectionId === sectionId)?.body;
  if (!body) return false;
  const matching = body.split(/[.!?](?=\s|$)|[;\n]| \| |\b(?:but|while|whereas|however|although|yet|and|or)\b/i)
    .map(normalize).filter(clause => exactValue.test(clause));
  return matching.length > 0 && matching.every(clause => {
    // A matching utility/service assertion is retained even when the same
    // clause also mentions a benchmark. Ambiguous mixed roles must reach the
    // ordinary source/conflict audit; this omission filter grants no authority.
    if (/\butility\b|\b(?:load[- ]service|load[- ]interconnection|system[- ]impact)\s+stud(?:y|ies)\b|\bservice[- ]letter\b/i.test(clause)) return false;
    // A denied benchmark role cannot classify an actual claim as context-only.
    if (/\b(?:not|no|never|without)\s+(?:a\s+)?(?:context[- ]only|proximity[- ]heuristic|proximity heuristic|(?:regional|generator|generation)\s+(?:queue\s+)?benchmarks?)\b/i.test(clause)) return false;
    return /\b(?:context[- ]only|proximity[- ]heuristic|proximity heuristic|(?:regional|generator|generation)\s+(?:queue\s+)?benchmarks?)\b/i.test(clause);
  });
}

/**
 * Equipment ratings, standby/BTM generator nameplate, transformer conversion
 * references, facility/IT demand, and multi-feeder aggregate envelope vs N-1
 * surviving-supply design comparisons are not `grid.estimatedCapacityMW`
 * utility headroom assertions.
 */
export function isNonHeadroomGridCapacityClaim(
  field: string,
  value: unknown,
  sectionId: string,
  sections: ReadonlyArray<{ sectionId: string; body: string }>,
): boolean {
  if (field !== 'grid.estimatedCapacityMW' || typeof value !== 'string') return false;
  const normalize = (text: string) => text.replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim().toLowerCase();
  const literal = normalize(value);
  if (!/^\d+(?:\.\d+)?\s*(?:mw|gw)$/.test(literal)) return false;
  const escaped = literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const exactValue = new RegExp(`(?<![\\d.])${escaped}(?![a-z0-9])`, 'i');
  const body = sections.find(section => section.sectionId === sectionId)?.body;
  if (!body) return false;
  const matching = body.split(/[.!?](?=\s|$)|[;\n]| \| /i)
    .map(normalize)
    .filter(clause => exactValue.test(clause));
  if (matching.length === 0) return false;
  return matching.every(clause => {
    if (/\b(?:available|confirmed|verified)\s+(?:utility\s+|grid\s+)?headroom\b/i.test(clause)) return false;
    return /\b(?:conversion\s+reference|modeled\s+path\s+loss|path\s+loss|transformer[- ]duty|primary\s+transformers?|diesel\s+standby|standby\s+(?:diesel|generators?|units?|duty)|installed\s+nameplate|one[- ]set[- ]out|aggregate\s+(?:installed\s+)?feed(?:er)?\s+(?:envelope|capacity)|n-1\s+surviving\s+supply|allocated\s+service|(?:site\s+)?service\s+allocation|grid\s+import|(?:utility|primary)\s+(?:feeds?|feeders?|paths?)|facility[- ]demand|facility\s+(?:demand|load)|it\s+load)\b/i.test(clause);
  });
}

/* ── Extractor ──────────────────────────────────────────────────────── */

export interface ExtractReportClaimsOptions {
  model?: string;
  runner?: typeof runGeminiJSON;
  timeoutMs?: number;
}

export async function extractReportClaims(
  report: SiteRagReport,
  sed: SiteEvidenceDocument,
  referencesMap: Map<number, string>,
  options: ExtractReportClaimsOptions = {},
): Promise<ReportClaim[]> {
  const targetFields = getTargetFieldsForReportType(report.reportType);
  if (targetFields.length === 0) return [];
  if (referencesMap.size === 0) return [];

  const sections = collectNarrativeSections(report);
  if (sections.length === 0) return [];

  const prompt = buildExtractionPrompt({ sections, targetFields, referencesMap });

  const runner = options.runner ?? runGeminiJSON;
  type RawResponse = {
    claims?: Array<{
      reportSectionId?: unknown;
      field?: unknown;
      value?: unknown;
      confidence?: unknown;
      sources?: unknown;
    }>;
  };

  const parsed = await runner<RawResponse>({
    model: options.model ?? DEFAULT_MODEL,
    contents: prompt,
    schema: CLAIM_SCHEMA,
    timeoutMs: options.timeoutMs,
  });

  if (!parsed || typeof parsed !== 'object') return [];
  const rawClaims = Array.isArray(parsed.claims) ? parsed.claims : [];
  void sed;

  const now = Date.now();
  const out: ReportClaim[] = [];

  for (let i = 0; i < rawClaims.length && out.length < MAX_CLAIMS; i += 1) {
    const claim = rawClaims[i];
    if (!claim || typeof claim !== 'object') continue;

    const sectionId = typeof claim.reportSectionId === 'string' ? claim.reportSectionId.trim() : '';
    const field = typeof claim.field === 'string' ? claim.field.trim() : '';
    const value = claim.value;
    const confidenceRaw = claim.confidence;
    const sourcesRaw = Array.isArray(claim.sources) ? claim.sources : [];

    if (!sectionId || !field) continue;
    if (isContextOnlyQueueClaim(field, value, sectionId, sections)) continue;
    if (isNonHeadroomGridCapacityClaim(field, value, sectionId, sections)) continue;

    const sources = sourcesRaw
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
      .map(s => s.trim());
    if (sources.length === 0) continue;

    const confidence = typeof confidenceRaw === 'number' && Number.isFinite(confidenceRaw)
      ? Math.max(0, Math.min(1, confidenceRaw))
      : 0.5;

    const id = `${report.id}.${sectionId}.${field}.${i}`;

    out.push({
      text: '',
      sources,
      sectionId,
      id,
      reportId: report.id,
      reportSectionId: sectionId,
      field,
      value,
      confidence,
      emittedAt: now,
    });
  }

  return out;
}

/* ── CSE-FOLLOWUP-INCREMENTAL (CI-1) ────────────────────────────────── */

/**
 * Byte-similarity threshold above which a section's prior claims are
 * reused on rerun. Pinned at 0.80. Below this falls back to fresh
 * extraction — pack rule 8 (no regression).
 */
export const INCREMENTAL_SIMILARITY_THRESHOLD = 0.8;

/**
 * Normalised byte-similarity in [0, 1] via character bigram Jaccard.
 * Identical → 1; disjoint → 0. Whitespace + case normalised.
 *
 * Bigram Jaccard (vs raw equality / Levenshtein):
 *  - Raw equality forces re-extract on any whitespace tweak.
 *  - Levenshtein is O(n·m) — too expensive on long sections.
 *  - Bigram Jaccard is O(n + m), stable on minor reflow, penalises
 *    paragraph swaps.
 */
export function computeByteSimilarity(a: string, b: string): number {
  const na = normalizeForSim(a);
  const nb = normalizeForSim(b);
  if (na.length === 0 && nb.length === 0) return 1;
  if (na.length === 0 || nb.length === 0) return 0;
  if (na === nb) return 1;
  if (na.length < 2 || nb.length < 2) return na === nb ? 1 : 0;

  const sa = bigramSet(na);
  const sb = bigramSet(nb);
  let inter = 0;
  const [small, big] = sa.size <= sb.size ? [sa, sb] : [sb, sa];
  for (const g of small) {
    if (big.has(g)) inter += 1;
  }
  const union = sa.size + sb.size - inter;
  if (union === 0) return 1;
  return inter / union;
}

function normalizeForSim(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function bigramSet(s: string): Set<string> {
  const out = new Set<string>();
  for (let i = 0; i < s.length - 1; i += 1) {
    out.add(s.slice(i, i + 2));
  }
  return out;
}

export interface SectionChangePartition {
  unchanged: ReadonlyArray<{ sectionId: string; similarity: number }>;
  changed: ReadonlyArray<{ sectionId: string; body: string; similarity: number }>;
}

/**
 * Partition current narrative sections into "unchanged" (≥ threshold
 * → reuse prior claims) and "changed" (must re-extract). New sections
 * (no prior body) classify as changed. Sections present in prior but
 * absent from current are dropped.
 * Pure.
 */
export function partitionSectionsByChange(
  current: ReadonlyArray<{ sectionId: string; body: string }>,
  prior: ReadonlyArray<{ sectionId: string; body: string }>,
  threshold: number = INCREMENTAL_SIMILARITY_THRESHOLD,
): SectionChangePartition {
  const priorBySection = new Map<string, string>();
  for (const p of prior) priorBySection.set(p.sectionId, p.body);

  const unchanged: Array<{ sectionId: string; similarity: number }> = [];
  const changed: Array<{ sectionId: string; body: string; similarity: number }> = [];
  for (const cur of current) {
    const priorBody = priorBySection.get(cur.sectionId);
    if (priorBody === undefined) {
      changed.push({ sectionId: cur.sectionId, body: cur.body, similarity: 0 });
      continue;
    }
    const sim = computeByteSimilarity(cur.body, priorBody);
    if (sim >= threshold) {
      unchanged.push({ sectionId: cur.sectionId, similarity: sim });
    } else {
      changed.push({ sectionId: cur.sectionId, body: cur.body, similarity: sim });
    }
  }

  return { unchanged, changed };
}

/**
 * Filter prior claims to those whose `reportSectionId` is in the
 * allow-list. Re-stamps each claim's `reportId` so RDX-1's recency
 * sort treats them as belonging to the rerun. Claims with no section
 * attribution are dropped. Pure.
 */
export function filterClaimsToUnchangedSections(
  priorClaims: ReadonlyArray<ReportClaim>,
  unchangedSectionIds: ReadonlySet<string>,
  reportId: string,
): ReportClaim[] {
  const out: ReportClaim[] = [];
  for (const claim of priorClaims) {
    const sec = typeof claim.reportSectionId === 'string' ? claim.reportSectionId : claim.sectionId;
    if (!sec || !unchangedSectionIds.has(sec)) continue;
    out.push({ ...claim, reportId });
  }
  return out;
}

export interface ExtractReportClaimsIncrementalOptions extends ExtractReportClaimsOptions {
  /** Override the similarity threshold (default 0.80). */
  similarityThreshold?: number;
}

export interface ExtractReportClaimsIncrementalResult {
  claims: ReportClaim[];
  reusedSectionIds: string[];
  reExtractedSectionIds: string[];
  /** True when zero Gemini calls were made (full reuse). */
  skippedExtractor: boolean;
}

/**
 * Incremental claim extraction. Reuses prior claims for byte-similar
 * sections; re-extracts only changed sections.
 *
 * Cost contract (pack rule 8):
 *  - All sections similar → zero Gemini calls.
 *  - All sections changed → one Gemini call against the full narrative
 *    (no regression vs the non-incremental path).
 *  - Mixed → one Gemini call against the CHANGED-only subset
 *    (strictly cheaper).
 *
 * Fail-soft: missing `priorReport` or empty `priorClaims` → full path.
 * Pure projection.
 */
export async function extractReportClaimsIncremental(
  report: SiteRagReport,
  sed: SiteEvidenceDocument,
  referencesMap: Map<number, string>,
  priorReport: SiteRagReport | undefined,
  priorClaims: ReadonlyArray<ReportClaim> | undefined,
  options: ExtractReportClaimsIncrementalOptions = {},
): Promise<ExtractReportClaimsIncrementalResult> {
  const threshold = options.similarityThreshold ?? INCREMENTAL_SIMILARITY_THRESHOLD;

  if (!priorReport || !priorClaims || priorClaims.length === 0) {
    const claims = await extractReportClaims(report, sed, referencesMap, options);
    const sections = collectNarrativeSections(report);
    return {
      claims,
      reusedSectionIds: [],
      reExtractedSectionIds: sections.map(s => s.sectionId),
      skippedExtractor: false,
    };
  }

  const targetFields = getTargetFieldsForReportType(report.reportType);
  if (targetFields.length === 0) {
    return { claims: [], reusedSectionIds: [], reExtractedSectionIds: [], skippedExtractor: true };
  }
  if (referencesMap.size === 0) {
    return { claims: [], reusedSectionIds: [], reExtractedSectionIds: [], skippedExtractor: true };
  }

  const currentSections = collectNarrativeSections(report);
  const priorSections = collectNarrativeSections(priorReport);
  if (currentSections.length === 0) {
    return { claims: [], reusedSectionIds: [], reExtractedSectionIds: [], skippedExtractor: true };
  }

  const partition = partitionSectionsByChange(currentSections, priorSections, threshold);
  const unchangedIds = new Set(partition.unchanged.map(u => u.sectionId));

  const reusedClaims = filterClaimsToUnchangedSections(priorClaims, unchangedIds, report.id)
    .filter(claim => !isContextOnlyQueueClaim(claim.field ?? '', claim.value, claim.reportSectionId ?? claim.sectionId ?? '', currentSections))
    .filter(claim => !isNonHeadroomGridCapacityClaim(claim.field ?? '', claim.value, claim.reportSectionId ?? claim.sectionId ?? '', currentSections));

  if (partition.changed.length === 0) {
    return {
      claims: reusedClaims,
      reusedSectionIds: partition.unchanged.map(u => u.sectionId),
      reExtractedSectionIds: [],
      skippedExtractor: true,
    };
  }

  const runner = options.runner ?? runGeminiJSON;
  const prompt = buildExtractionPrompt({
    sections: partition.changed.map(c => ({ sectionId: c.sectionId, body: c.body })),
    targetFields,
    referencesMap,
  });

  type RawResponse = {
    claims?: Array<{
      reportSectionId?: unknown;
      field?: unknown;
      value?: unknown;
      confidence?: unknown;
      sources?: unknown;
    }>;
  };

  const parsed = await runner<RawResponse>({
    model: options.model ?? DEFAULT_MODEL,
    contents: prompt,
    schema: CLAIM_SCHEMA,
    timeoutMs: options.timeoutMs,
  });

  void sed;

  const freshClaims: ReportClaim[] = [];
  if (parsed && typeof parsed === 'object') {
    const rawClaims = Array.isArray(parsed.claims) ? parsed.claims : [];
    const now = Date.now();
    const changedIds = new Set(partition.changed.map(c => c.sectionId));
    for (let i = 0; i < rawClaims.length && freshClaims.length < MAX_CLAIMS; i += 1) {
      const claim = rawClaims[i];
      if (!claim || typeof claim !== 'object') continue;
      const sectionId = typeof claim.reportSectionId === 'string' ? claim.reportSectionId.trim() : '';
      const field = typeof claim.field === 'string' ? claim.field.trim() : '';
      if (!sectionId || !field) continue;
      // Defensive: drop claims targeting an unchanged section so the
      // reuse invariant stays intact.
      if (!changedIds.has(sectionId)) continue;
      if (isContextOnlyQueueClaim(field, claim.value, sectionId, currentSections)) continue;
      if (isNonHeadroomGridCapacityClaim(field, claim.value, sectionId, currentSections)) continue;

      const sourcesRaw = Array.isArray(claim.sources) ? claim.sources : [];
      const sources = sourcesRaw
        .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        .map(s => s.trim());
      if (sources.length === 0) continue;

      const confidenceRaw = claim.confidence;
      const confidence = typeof confidenceRaw === 'number' && Number.isFinite(confidenceRaw)
        ? Math.max(0, Math.min(1, confidenceRaw))
        : 0.5;

      freshClaims.push({
        text: '',
        sources,
        sectionId,
        id: `${report.id}.${sectionId}.${field}.${i}`,
        reportId: report.id,
        reportSectionId: sectionId,
        field,
        value: claim.value,
        confidence,
        emittedAt: now,
      });
    }
  }

  return {
    claims: [...reusedClaims, ...freshClaims],
    reusedSectionIds: partition.unchanged.map(u => u.sectionId),
    reExtractedSectionIds: partition.changed.map(c => c.sectionId),
    skippedExtractor: false,
  };
}

/* ── CSE-FOLLOWUP-CONFLICT-DETECTION (CI-2) ─────────────────────────── */

export interface ClaimConflict {
  reportSectionId: string;
  field: string;
  /** Deduped string forms of the distinct values observed. */
  values: string[];
}

/**
 * Detect internally-conflicting claims — two or more claims that share
 * (reportSectionId, field) but disagree on `value`. Returns one entry
 * per collision with the full set of distinct normalised values.
 *
 * Normalisation:
 *  - `String(value).trim()`.
 *  - Empty normalised forms ignored.
 *
 * Pure. Used by `applyClaimsEmission` post-merge so the wire-up site
 * can stamp `report.qualityWarnings += [{ type: 'cse-internal-conflict',
 * … }]` for the admin chip to surface.
 */
const EQUIVALENT_UNKNOWN_CLAIM_VALUE_RE =
  /^(?:not\s+(?:established|verified|confirmed|determined|available|specified|applicable|disclosed)|unverified|unresolved|unknown|unconfirmed|absent|undetermined|tbd|n\/a|pending(?:\s+(?:study|verification|review))?|no\s+(?:executed|verified|confirmed|signed|binding)\s+(?:lgia|interconnection(?:\s+agreement)?|load[- ]service\s+agreement)|not\s+an?\s+(?:executed|signed|accepted|confirmed|verified|binding)\s+(?:lgia|interconnection\s+agreement|load[- ]service\s+agreement|utility\s+agreement|queue\s+commitment)(?:\s+or\s+(?:accepted|executed|signed|confirmed|verified)\s+(?:lgia|interconnection\s+agreement|load[- ]service\s+agreement))?)$/i;

export function isEquivalentUnknownClaimValue(raw: string | null | undefined): boolean {
  const s = (raw ?? '').trim().replace(/[.]+$/, '').trim();
  if (!s) return false;
  return EQUIVALENT_UNKNOWN_CLAIM_VALUE_RE.test(s);
}

export function areAllEquivalentUnknownClaimValues(
  values: ReadonlyArray<string> | null | undefined,
): boolean {
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.every((v) => isEquivalentUnknownClaimValue(v));
}

export function detectClaimConflicts(
  claims: ReadonlyArray<ReportClaim>,
): ClaimConflict[] {
  const buckets = new Map<
    string,
    { reportSectionId: string; field: string; values: Set<string> }
  >();
  for (const claim of claims) {
    const sec = typeof claim.reportSectionId === 'string' ? claim.reportSectionId.trim() : '';
    const field = typeof claim.field === 'string' ? claim.field.trim() : '';
    if (!sec || !field) continue;
    const raw = claim.value;
    const normalised = raw == null ? '' : String(raw).trim();
    if (normalised.length === 0) continue;

    const key = `${sec}:::${field}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { reportSectionId: sec, field, values: new Set<string>() };
      buckets.set(key, bucket);
    }
    bucket.values.add(normalised);
  }

  const out: ClaimConflict[] = [];
  for (const { reportSectionId, field, values } of buckets.values()) {
    if (values.size < 2) continue;
    const arr = Array.from(values);
    if (areAllEquivalentUnknownClaimValues(arr)) continue;
    out.push({ reportSectionId, field, values: arr });
  }
  return out;
}

/* ── Wire-up helper (CSE-2) ─────────────────────────────────────────── */

export interface ApplyClaimsEmissionOptions extends ExtractReportClaimsIncrementalOptions {
  /** Trace tag (e.g. 'typed' / 'legacy') for the console.log breadcrumb. */
  pathTag?: string;
  /**
   * CSE-FOLLOWUP-CONFIDENCE-CALIBRATION (CL-1) — optional per-section-type
   * confidence cap map. When supplied, the extractor's emitted claims
   * have `confidence = min(confidence, cap)` applied via
   * `applyCalibrationToClaims`. Sections without an entry pass through
   * unchanged. Pure projection: legacy CT6 claims (no `field`) and
   * pre-existing `report.claims` entries are NOT touched.
   *
   * Loaded from Firestore via `loadCalibrationFloors`; gen-time
   * callers typically resolve this once per report (one Firestore
   * read) and pass through.
   *
   * When undefined the behaviour is identical to pre-CL-1 — the
   * applied path is a no-op when the map is empty.
   */
  calibrations?: ReadonlyMap<string, import('./claimCalibration').ClaimConfidenceCalibration>;
  /**
   * CI-1 — prior report for incremental extraction. With `priorClaims`
   * triggers the incremental path. Absent → full path (no regression).
   */
  priorReport?: SiteRagReport;
  /**
   * CI-1 — prior report's persisted CSE-shape claims. Required for the
   * incremental path; absent → falls back to full extraction.
   */
  priorClaims?: ReadonlyArray<ReportClaim>;
  /**
   * CI-2 — invoked AFTER the merge with any detected conflicts (empty
   * array means clean). The wire-up site uses this to stamp
   * `report.qualityWarnings += [{ type: 'cse-internal-conflict', … }]`
   * without this module taking a dependency on the report-mutation
   * surface. Always invoked (even with []) so callers can clear stale
   * warnings idempotently. Pure projection — never modifies inputs.
   */
  onConflicts?: (conflicts: ClaimConflict[]) => void;
}

export async function applyClaimsEmission(
  report: SiteRagReport,
  sed: SiteEvidenceDocument,
  options: ApplyClaimsEmissionOptions = {},
): Promise<ReportClaim[]> {
  const existing = Array.isArray(report.claims) ? report.claims : [];

  try {
    const refsMap = buildReferencesMap(report.references ?? [], sed);

    const guard = (report as { sectionGuardStatus?: Record<string, string> }).sectionGuardStatus ?? {};
    let reportForExtractor: SiteRagReport = report;
    if (Object.values(guard).some(v => v === 'degraded-instruction-leak')) {
      const stripped: Record<string, unknown> = { ...(report as unknown as Record<string, unknown>) };
      for (const [k, v] of Object.entries(guard)) {
        if (v === 'degraded-instruction-leak' && typeof stripped[k] === 'string') {
          stripped[k] = '';
        }
      }
      reportForExtractor = (stripped as unknown) as SiteRagReport;
    }

    let rawCseClaims: ReportClaim[];
    let breadcrumb = '';

    if (options.priorReport && options.priorClaims && options.priorClaims.length > 0) {
      // CI-1 — incremental: reuse byte-similar sections' prior claims,
      // re-extract only the changed sections.
      const incremental = await extractReportClaimsIncremental(
        reportForExtractor,
        sed,
        refsMap,
        options.priorReport,
        options.priorClaims,
        options,
      );
      rawCseClaims = incremental.claims;
      breadcrumb =
        ` reused=${incremental.reusedSectionIds.length}` +
        ` reExtracted=${incremental.reExtractedSectionIds.length}` +
        ` skippedExtractor=${incremental.skippedExtractor}`;
    } else {
      rawCseClaims = await extractReportClaims(reportForExtractor, sed, refsMap, options);
    }

    // CSE-FOLLOWUP-CONFIDENCE-CALIBRATION (CL-1) — narrow confidence
    // per section type using admin-flagged "still wrong" feedback.
    // Pass-through when the option is absent OR the map is empty so
    // the pre-CL-1 behaviour is preserved exactly.
    let cseClaims = rawCseClaims;
    if (options.calibrations && options.calibrations.size > 0) {
      const { applyCalibrationToClaims } = await import('./claimCalibration');
      cseClaims = applyCalibrationToClaims(rawCseClaims, options.calibrations);
    }

    const merged = [...existing, ...cseClaims];

    // CI-2 — detect (sectionId, field) conflicts post-merge so the
    // caller's onConflicts hook can stamp `qualityWarnings`. Pure.
    // Always fires (even with []) so callers can clear stale warnings.
    if (options.onConflicts) {
      const conflicts = detectClaimConflicts(merged);
      options.onConflicts(conflicts);
    }

    const tag = options.pathTag ? ` (${options.pathTag})` : '';
    console.log(`[cse:emitted] reportId=${report.id} cseClaims=${cseClaims.length}${tag}${breadcrumb}`);
    return merged;
  } catch (e) {
    console.warn('[cse:failed]', (e as Error)?.message);
    return existing;
  }
}

/* ── Health helpers (CSE-3 admin surface) ───────────────────────────── */

export interface ClaimEmissionHealth {
  emittedCount: number;
  emittedEmptyCount: number;
  absentCount: number;
  averageClaimsPerEmittedReport: number;
  averageSourcesPerClaim: number;
  emptyRate: number;
  emptyRateAlert: boolean;
}

const EMPTY_RATE_ALERT_THRESHOLD = 0.1;

export function summariseClaimEmission(
  reports: ReadonlyArray<SiteRagReport>,
): ClaimEmissionHealth {
  let emitted = 0;
  let emittedEmpty = 0;
  let absent = 0;
  let totalStructuredClaims = 0;
  let totalSourceCount = 0;

  for (const report of reports) {
    const claims = report.claims;
    if (!Array.isArray(claims)) {
      absent += 1;
      continue;
    }
    const structured = claims.filter(c => typeof c.field === 'string' && c.field.length > 0);
    if (structured.length === 0) {
      emittedEmpty += 1;
      continue;
    }
    emitted += 1;
    totalStructuredClaims += structured.length;
    for (const c of structured) {
      totalSourceCount += Array.isArray(c.sources) ? c.sources.length : 0;
    }
  }

  const denom = emitted + emittedEmpty;
  const emptyRate = denom === 0 ? 0 : emittedEmpty / denom;

  return {
    emittedCount: emitted,
    emittedEmptyCount: emittedEmpty,
    absentCount: absent,
    averageClaimsPerEmittedReport: emitted === 0 ? 0 : totalStructuredClaims / emitted,
    averageSourcesPerClaim: totalStructuredClaims === 0 ? 0 : totalSourceCount / totalStructuredClaims,
    emptyRate,
    emptyRateAlert: emptyRate > EMPTY_RATE_ALERT_THRESHOLD,
  };
}
